import React from 'react';
import PropTypes from 'prop-types';
import * as d3 from 'd3';
import { sankey as d3Sankey, sankeyCenter } from 'd3-sankey';
import isDev from 'Helpers/isDev';
import { useTheme } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { convertFromResourceToData } from './conversion';
import { buildTestData } from './testData';

const spacing = (v) => v * 8;

const HEADER_HEIGHT = 24;
const ICON_SIZE = 24;
const LINE_WIDTH = 200;
const LINE_HEIGHT = 1.2; // em
const TRANSITION_DURATION = 4000;
const EASING = d3.easeQuadOut;
const DEBUG_RECT = false;

const SVG_MARGIN = {
  top: spacing(0),
  bottom: spacing(0),
  left: spacing(0),
  right: spacing(0),
};

const SANKEY_MARGIN = {
  top: spacing(3),
  bottom: spacing(1),
  left: ICON_SIZE + spacing(2),
  right: spacing(1),
};

const getNumPaths = (data) => {
  const columnSemantics = d3.group(data.nodes, d => d.semantic);
  return columnSemantics.get('account-name').length;
};

const getCombinedValue = (v) => [v.left + v.right, v.top + v.bottom];

const getLineInfo = (words, maxLineWidth, avgCharWidth) => {
  return words.reduce((acc, currWord) => {
    const currLine = acc[acc.length - 1];
    const wordWidth = currWord.length * avgCharWidth;

    if (currLine.estimatedWidth + wordWidth > maxLineWidth) {
      acc.push({ estimatedWidth: wordWidth, words: [currWord], lineNumber: currLine.lineNumber + 1 });
    } else {
      currLine.estimatedWidth += wordWidth;
      currLine.words.push(currWord);
    }

    return acc;
  }, [{ estimatedWidth: 0, lineNumber: 0, words: [] }]);
};

const getNodeFontSize = (d) => (d.semantic === 'result' ? '1.5rem' : '1rem');
const getNodeFontWeight = (d) => (d.semantic === 'result' ? '500' : '400');

// Based on: https://stackoverflow.com/questions/73715465/how-to-wrap-or-break-long-text-in-a-fixed-width-on-d3-js-chart-library-javascr
function wrapText(textNode, width) {
  // eslint-disable-next-line func-names
  textNode.each(function () {
    const text = d3.select(this);

    const originalWidth = text.node().getBBox().width;
    const countChars = text.text().length;

    // quit early if the line fits
    if (text.node().getBBox().width < width) {
      return undefined;
    }

    const avgCharWidth = Math.ceil(originalWidth / countChars);
    const words = text.text().split(/\s+/);

    const lineInfo = getLineInfo(words, width, avgCharWidth);

    // Add one more to total lines because the first line is first translated up by the text y attr below and then translated back down by its dy.

    text.text(null)
      .attr('y', `${-1 * LINE_HEIGHT}em`)
      .selectAll('tspan')
      .data(lineInfo)
      .enter()
      .append('tspan')
      .text(d => d.words.join(' '))
      .attr('x', 0)
      .attr('dy', () => {
        return `${LINE_HEIGHT}em`;
      });
    return undefined;
  });
}

const draw = (chartId, svgWidth, svgHeight, data, theme, backgroundColor, fromLabel = 'Move from', amountLabel = 'Amount', toLabel = 'Move to') => {
  const addSvgElement = () => {
    return d3.select(`#${chartId}`).append('svg')
      .attr('width', svgWidth)
      .attr('height', svgHeight);
  };

  const addTopLevelGroups = (svg) => {
    const headerGroup = svg.append('g')
      .attr('data-semantic', 'header')
      .attr('transform', `translate(${SVG_MARGIN.left},${SVG_MARGIN.top})`);

    const sankeyGroup = svg.append('g')
      .attr('data-semantic', 'sankey')
      .attr('transform', `translate(${SVG_MARGIN.left},${SVG_MARGIN.top})`);

    return [headerGroup, sankeyGroup];
  };

  const calculateSankey = (svg, graph) => {
    const [sankeyMarginHorizontal, sankeyMarginVertical] = getCombinedValue(SANKEY_MARGIN);
    const [svgMarginHorizontal, svgMarginVertical] = getCombinedValue(SVG_MARGIN);

    const resultGraphNodes = graph.nodes.filter(v => v.semantic === 'result');


    if (resultGraphNodes.length !== 1) {
      throw new Error('Must have exactly 1 result node');
    }

    const avgCharWidth = 10;
    const resultWidth = resultGraphNodes[0].label.length * avgCharWidth;

    const sankey = d3Sankey()
      .nodeWidth(0)
      .nodePadding(20)
      .nodeAlign(sankeyCenter)
      .nodeId(d => d.nodeId)
      .extent([
        [SANKEY_MARGIN.left + SVG_MARGIN.left, SANKEY_MARGIN.top + SVG_MARGIN.top],
        [
          svgWidth - svgMarginHorizontal - sankeyMarginHorizontal - resultWidth,
          svgHeight - svgMarginVertical - sankeyMarginVertical,
        ],
      ]);

    sankey(graph);
  };

  const midpoint = (a, b) => ((a + b) / 2);

  // https://bl.ocks.org/mbostock/1705868
  const translateAlong = (paths) => {
    const lengths = paths.map(v => v.getTotalLength());
    const totalLength = lengths.reduce((acc, curr) => acc + curr, 0);

    const getPathAndPosition = (dist) => {
      let remainingDist = dist;

      for (let i = 0; i < paths.length; i++) {
        if (remainingDist <= lengths[i]) {
          return { pathIdx: i, path: paths[i], distance: remainingDist };
        }
        remainingDist -= lengths[i];
      }

      if (isDev()) {
        throw new Error('This should not be possible');
      }

      // If for some reason it is in fact possible to end up down here
      // then just pick the start of the first path if you aren't sure where to draw the dot in prod
      return { pathIdx: 0, path: paths[0], distance: lengths[0] };
    };

    return function calculateTranslation(t) {
      const { path, distance } = getPathAndPosition(t * totalLength);
      const { x, y } = path.getPointAtLength(distance);
      return `translate(${x},${y})`;
    };
  };

  const drawLinks = (svg, graph) => {
    const linkGroupData = d3.group(graph.links, d => d.pathId);

    const linkGroups = svg.append('g')
      .attr('data-semantic', 'links')
      .selectAll('[data-semantic="link"]')
      .data(linkGroupData)
      .enter()
      .append('g')
      .attr('class', 'link')
      .attr('data-semantic', 'link')
      .attr('data-path-id', d => {
        return d[0];
      })
      .sort((a, b) => { return b.y0 - a.y0; });

    // Draw the paths
    linkGroups
      .selectAll('path')
      .data(d => d[1])
      .enter()
      .append('path')
      .attr('data-semantic', 'link-path')
      .attr('d', d3.linkHorizontal().source((d) => {
        return [d.source.x1, midpoint(d.source.y1, d.source.y0)];
      }).target((d) => {
        return [d.target.x0, midpoint(d.target.y1, d.target.y0)];
      }))
      .style('stroke-width', 2)
      .style('stroke', theme.palette.facet.grey500)
      .style('stroke-dasharray', '3,3')
      .style('fill-opacity', 0);

    // Draw one circle per set of paths for each path group
    const circle = linkGroups
      .append('circle')
      .attr('fill', theme.palette.facet.grey500)
      .attr('r', 4);

    function repeat() {
      circle
        .attr('transform', (d) => {
          const [, linkData] = d;
          const firstPathData = linkData[0]; // Guaranteed to have one of these things. We want the first one so we start by drawing at the beginning
          return `translate(${firstPathData.source.x0},${midpoint(firstPathData.source.y0, firstPathData.source.y1)})`;
        })
        .transition()
        .ease(EASING)
        .duration(TRANSITION_DURATION)
        .attrTween('transform', d => {
          const [pathId] = d;
          const path = svg.select(`[data-path-id='${pathId}']`).selectAll('path');
          return translateAlong(path.nodes());
        })
        .on('end', repeat);
    }

    repeat();
  };

  const drawNodeBackgroundPlaceholders = (node) => {
    node.append('rect')
      .attr('x', 0)
      .attr('y', (d) => {
        return ((d.y1 - d.y0) / 2);
      });
  };

  const appendNodes = (svg, graph) => {
    return svg.append('g')
      .attr('data-semantic', 'nodes')
      .selectAll('.node')
      .data(graph.nodes)
      .enter()
      .append('g')
      .attr('data-semantic', (d) => d.semantic)
      .attr('class', 'node')
      .attr('transform', (d) => {
        return `translate(${d.x0},${midpoint(d.y0, d.y1)})`;
      });
  };

  const drawNodeText = (svg, node) => {
    const primaryText = node
      .append('text')
      .attr('x', 0)
      .attr('y', 0)
      .attr('dominant-baseline', 'central')
      .attr('transform', null)
      .attr('fill', theme.palette.facet.oceana800)
      .attr('font-size', getNodeFontSize)
      .attr('font-weight', getNodeFontWeight)
      .text((d) => { return d.label; })
      .call(wrapText, LINE_WIDTH);

    node
      .filter(d => d.interval)
      .append('text')
      .attr('x', 0)
      .attr('y', '-1.5em')
      .attr('dominant-baseline', 'central')
      .attr('transform', null)
      .attr('fill', theme.palette.facet.oceana800)
      .attr('font-size', '12px')
      .attr('random', 'interval')
      .attr('font-weight', getNodeFontWeight)
      .text((d) => { return d.interval; });

    primaryText
      .each(function appendBBox(d) {
      // eslint-disable-next-line no-param-reassign
        d.bbox = this.getBBox();
      });

    svg
      .selectAll('[data-semantic="account-name"]')
      .each(function appendIconChild(d) {
        return this.appendChild(document.querySelector(`[data-icon="${d.icon}"]`).cloneNode(true));
      });

    svg
      .selectAll('[data-semantic="visualization-icon"]')
      .attr('x', -1 * (SANKEY_MARGIN.left))
      .attr('y', '-0.35em')
      .attr('width', ICON_SIZE)
      .attr('height', ICON_SIZE);
  };

  const alignAmountNodes = (svg) => {
  // Select all amount nodes and translate them to be right aligned
    const widths = svg
      .selectAll('[data-semantic="amount"]').data().map(d => d.bbox.width);

    const minWidth = Math.min(...widths);

    svg.selectAll('[data-semantic="amount"]')
      .attr('transform', (d) => {
        const startingX = d.x0;
        const { width: bboxWidth } = d.bbox;
        const x = startingX + minWidth - bboxWidth;

        return `translate(${x},${midpoint(d.y0, d.y1)})`;
      });
  };

  const repositionAndResizeNodesInTextGroups = (node) => {
    const PADDING = {
      left: spacing(1),
      right: spacing(1),
      top: spacing(0.5), //  A change to this value will yTranslation below. Make sure to turn on DEBUG_RECT and reconfigure it.
      yTranslation: '-0.9em',
      bottom: spacing(0.5),
    };

    const PADDING_HORIZONTAL = PADDING.left + PADDING.right;
    const PADDING_VERTICAL = PADDING.top + PADDING.bottom;

    // All text is drawn on top of a rect
    node.select('rect')
      .attr('x', () => {
        const paddingTranslation = PADDING.left;
        return 0 - paddingTranslation;
      })
      .attr('y', PADDING.yTranslation)
      .attr('width', (d) => {
        return d.bbox?.width + PADDING_HORIZONTAL;
      })
      .attr('height', (d) => {
        return d.bbox?.height + PADDING_VERTICAL;
      })
      .style('fill', DEBUG_RECT ? 'pink' : backgroundColor)
      .style('rx', 8)
      .style('ry', 8);
  };

  const drawNodes = (svg, graph) => {
    const node = appendNodes(svg, graph);
    drawNodeBackgroundPlaceholders(node);
    drawNodeText(svg, node);
    alignAmountNodes(svg);
    repositionAndResizeNodesInTextGroups(node);
  };

  const drawHeader = (headerGroup, graph) => {
    const columnHeaderData = d3.rollup(graph.nodes, v => v[0], d => d.semantic);

    headerGroup.append('line')
      .style('stroke', theme.palette.facet.orange)
      .style('stroke-width', 1)
      .attr('x1', 0)
      .attr('y1', HEADER_HEIGHT)
      .attr('x2', svgWidth - SVG_MARGIN.left)
      .attr('y2', HEADER_HEIGHT);

    headerGroup
      .selectAll('.header-label')
      .data(columnHeaderData)
      .enter()
      .append('text')
      .attr('data-semantic', (d) => d.semantic)
      .attr('fill', theme.palette.facet.oceana800)
      .attr('font-size', '0.75rem')
      .attr('font-weight', '600')
      .attr('class', 'header-label')
      .attr('y', HEADER_HEIGHT)
      .attr('dy', -spacing(1.5))
      .attr('text-anchor', (v) => {
        const [semantic] = v;

        switch (semantic) {
          case 'result':
            return 'end';
          default:
            return 'start';
        }
      })
      .attr('transform', (v) => {
        const [semantic, d] = v;
        let xTranslation;

        switch (semantic) {
          case 'account-name':
            xTranslation = d.x0 - SANKEY_MARGIN.left;
            break;
          case 'result':
            xTranslation = svgWidth - SVG_MARGIN.left;
            break;
          default:
          case 'amount':
            xTranslation = d.x0;
        }
        return `translate(${xTranslation},0)`;
      })
      .text(v => {
        const [semantic] = v;
        switch (semantic) {
          case 'account-name':
            return fromLabel;
          case 'amount':
            return amountLabel;
          case 'result':
            return toLabel;
          default:
            if (isDev()) {
              throw new Error(`Unexpected semantic: ${semantic}`);
            }

            return semantic;
        }
      });
  };

  // Remove existing
  d3.select(`#${chartId}`).select('svg').remove();

  const graph = data;
  const svg = addSvgElement(chartId);

  calculateSankey(svg, graph);
  const [headerGroup, sankeyGroup] = addTopLevelGroups(svg);
  drawHeader(headerGroup, graph);
  drawLinks(sankeyGroup, graph);
  drawNodes(sankeyGroup, graph);
};

export const Visualization = ({
  chartId, width, resource, backgroundColor,
}) => {
  const theme = useTheme();
  const data = React.useMemo(() => {
    if (resource) {
      return convertFromResourceToData(resource);
    }

    if (!isDev()) {
      throw new Error('You must supply a resource');
    }

    return buildTestData();
  }, [resource]);

  const countPaths = getNumPaths(data);
  const height = (countPaths === 1) ? 150 : 150 + (countPaths - 1) * 80;

  const fromLabel = resource.attr('moveFromLabel');
  const amountLabel = resource.attr('amountLabel');
  const toLabel = resource.attr('moveToLabel');

  React.useEffect(() => {
    draw(chartId, width, height, data, theme, backgroundColor, fromLabel, amountLabel, toLabel);
  }, [chartId, height, width, data, theme, backgroundColor, fromLabel, amountLabel, toLabel]);

  return (
    <div id={chartId} />
  );
};

Visualization.propTypes = {
  chartId: PropTypes.string.isRequired,
  width: PropTypes.number.isRequired,
  resource: PropTypes.object.isRequired,
};
